import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { formatYuan } from '../utils/money';

// 报价结算净利率报表（报价-成本闭环，2026-09 迭代A）
// 数据源：GET /api/reports/quotation-margin，契约 { quotations: [...], summary: {...} }。
// 只统计「关联了项目」或「手填过实际成本」的报价单；净利率阈值 30% 是业务铁律（后端常量）。
export default function QuotationMarginReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // 财务页底线：加载失败必须显式报错+重试，不许渲染成「全 0 报表」
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const response = await api.get('/api/reports/quotation-margin');
      setRows(response.data?.quotations ?? []);
      setSummary(response.data?.summary ?? null);
    } catch (error) {
      console.error('Error fetching quotation margin:', error);
      setLoadError(error.response?.data?.error || '报价净利率数据加载失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>报价结算净利率</Typography>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={fetchData}>重试</Button>}
        >
          {loadError}
        </Alert>
      </Box>
    );
  }

  const withMargin = rows.filter((r) => r.marginPct !== null);
  const summaryCards = [
    {
      label: '纳入结算的单数',
      value: `${summary?.count ?? rows.length} 单`,
      hint: summary?.count ? `其中净利率可算 ${withMargin.length} 单` : '关联项目或手填成本后进入统计',
    },
    {
      label: `达标率（净利率 ≥ ${summary?.thresholdPct ?? 30}%）`,
      value: summary?.meetRatePct !== null && summary?.meetRatePct !== undefined ? `${summary.meetRatePct}%` : '—',
      hint: summary ? `${summary.meetCount ?? 0} / ${withMargin.length} 单达标` : '',
    },
    {
      label: '平均净利率（报价口径）',
      value: summary?.avgMarginPct !== null && summary?.avgMarginPct !== undefined ? `${summary.avgMarginPct}%` : '—',
      hint: '（含税总价-实际成本）/含税总价',
    },
    {
      label: '报价合计 / 实际成本合计',
      value: summary ? `${formatYuan(summary.totalQuoteYuan || 0)} / ${formatYuan(summary.totalActualCostYuan || 0)}` : '—',
      hint: '全部纳入结算的单',
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>报价结算净利率</Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryCards.map((c) => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="h6">{c.value}</Typography>
                <Typography variant="caption" color="text.secondary">{c.hint}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {rows.length === 0 ? (
        <Alert severity="info">
          暂无纳入结算的报价单。在报价单详情页「编辑」里关联项目（成本自动汇总），或在「报价结算」卡手填实际成本，本报表即出数。
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>报价单号</TableCell>
                <TableCell>工程名称</TableCell>
                <TableCell>关联项目</TableCell>
                <TableCell align="right">含税总价</TableCell>
                <TableCell align="right">实际成本</TableCell>
                <TableCell align="right">成本来源</TableCell>
                <TableCell align="right">净利率（报价口径）</TableCell>
                <TableCell align="right">净利率（回款口径）</TableCell>
                <TableCell align="center">达标</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/quotations/${r.id}`)}
                >
                  <TableCell>{r.quotationNo || '-'}</TableCell>
                  <TableCell>{r.projectName || '-'}</TableCell>
                  <TableCell>{r.linkedProjectName || '-'}</TableCell>
                  <TableCell align="right">{formatYuan(r.totalAmountYuan || 0)}</TableCell>
                  <TableCell align="right">{r.actualCostYuan !== null && r.actualCostYuan !== undefined ? formatYuan(r.actualCostYuan) : '—'}</TableCell>
                  <TableCell align="right">
                    {r.costSource === 'override' ? '手填' : r.costSource === 'system' ? '项目汇总' : '无'}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 'bold', color: r.meetsThreshold === false ? 'error.main' : r.meetsThreshold === true ? 'success.main' : 'inherit' }}
                  >
                    {r.marginPct !== null ? `${r.marginPct}%` : '—'}
                  </TableCell>
                  <TableCell align="right">{r.cashMarginPct !== null ? `${r.cashMarginPct}%` : '—'}</TableCell>
                  <TableCell align="center">
                    {r.meetsThreshold === null || r.meetsThreshold === undefined ? (
                      <Chip label="无成本" size="small" color="default" variant="outlined" />
                    ) : (
                      <Chip label={r.meetsThreshold ? '≥30%' : '<30%'} size="small" color={r.meetsThreshold ? 'success' : 'error'} variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        口径：净利率（报价口径）=（含税总价-实际成本）/含税总价；净利率（回款口径）=（回款-实际成本）/回款，未回款显示 —。
        实际成本优先取手填结算值，否则取关联项目的记账汇总（人工+材料+杂费）。点击行进入报价单详情的「报价结算」卡可回填。
      </Typography>
    </Box>
  );
}
